import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import s from "./Alerts.module.css";
import { Page } from "../../layout/AppShell";
import { useSession } from "../../app/SessionContext";
import { useAlertExplanation, useAlerts, useWorkOrders } from "../../api/queries";
import { hasPermission, PERMISSION } from "../../api/dataSource";
import {
  priorityForSeverity,
  severityLabel,
  sortAlerts,
  type Alert,
  type AlertExplanation,
  type AlertKind,
} from "../../domain/alerts";
import { Button, Card, ErrorState, Segmented, SeverityBadge, Skeleton } from "../../design/components";
import { formatDate, formatDateTime } from "../../lib/format";
import { t } from "../../i18n";
import { CreateWorkOrderDialog, type WorkOrderPrefill } from "../work-orders/CreateWorkOrderDialog";

type KindFilter = "all" | AlertKind;

export function AlertsPage() {
  const { site } = useSession();
  const [params, setParams] = useSearchParams();
  const kindFilter = (params.get("kind") as KindFilter) || "all";
  const openId = params.get("open");

  const alerts = useAlerts(kindFilter === "all" ? undefined : kindFilter);
  const sorted = useMemo(() => sortAlerts(alerts.data ?? []), [alerts.data]);

  /* Open the first alert when none is chosen, so the explanation is never an empty pane. */
  useEffect(() => {
    if (!openId && sorted.length) {
      const next = new URLSearchParams(params);
      next.set("open", sorted[0].id);
      setParams(next, { replace: true });
    }
  }, [openId, sorted, params, setParams]);

  const setKind = (k: KindFilter) => {
    const next = new URLSearchParams(params);
    if (k === "all") next.delete("kind");
    else next.set("kind", k);
    setParams(next, { replace: true });
  };
  const open = (id: string) => {
    const next = new URLSearchParams(params);
    next.set("open", id);
    setParams(next, { replace: true });
  };

  return (
    <Page eyebrow={t("{code} · insight", { code: site.code })} title="Alerts">
      <div className={s.layout}>
        <div style={{ display: "grid", gap: "var(--gap)" }}>
          <div className={s.filters}>
            <Segmented<KindFilter>
              ariaLabel="Kind"
              value={kindFilter}
              options={[
                { value: "all", label: "All open" },
                { value: "reactive", label: "Reactive" },
                { value: "predictive", label: "Predictive" },
              ]}
              onChange={setKind}
            />
            <span className={s.count}>{alerts.data ? t("{n} open", { n: sorted.length }) : ""}</span>
          </div>
          {alerts.isPending ? (
            <Skeleton rows={5} height={44} />
          ) : alerts.isError ? (
            <ErrorState error={alerts.error} retry={() => void alerts.refetch()} />
          ) : sorted.length === 0 ? (
            <div className={s.empty}>
              {t("No open alerts at {code}.", { code: site.code })}
            </div>
          ) : (
            <div className={s.list}>
              {sorted.map((a) => (
                <AlertRow key={a.id} alert={a} current={a.id === openId} onOpen={() => open(a.id)} />
              ))}
            </div>
          )}
        </div>

        <div className={s.explain}>
          {openId ? (
            <Explanation id={openId} />
          ) : (
            <div className={s.empty}>{t("Select an alert to see why it fired.")}</div>
          )}
        </div>
      </div>
    </Page>
  );
}

/** The kind as the row has always written it, lower case. */
const KIND_WORD: Record<AlertKind, string> = { reactive: "reactive", predictive: "predictive" };

function AlertRow({ alert, current, onOpen }: { alert: Alert; current: boolean; onOpen: () => void }) {
  return (
    <button type="button" className={s.row} aria-current={current} onClick={onOpen}>
      <SeverityBadge severity={alert.severity} />
      <div>
        <div className={s.rowTitle}>{alert.title}</div>
        <div className={s.rowMeta}>
          <span className={alert.kind === "predictive" ? s.predictive : undefined}>{t(KIND_WORD[alert.kind])}</span>
          <span>{t(severityLabel(alert.severity))}</span>
          {alert.ruleKey && <span>{alert.ruleKey}</span>}
          {alert.assetTag && <span>{alert.assetTag}</span>}
          <span>{t("raised {date}", { date: formatDate(alert.raisedAt) })}</span>
          {alert.predictedFor && <span>{t("expected {date}", { date: formatDate(alert.predictedFor) })}</span>}
          {alert.confidence != null && (
            <span>{t("{pct}% confidence", { pct: Math.round(alert.confidence * 100) })}</span>
          )}
        </div>
      </div>
    </button>
  );
}

/**
 * Why did this fire? The rule, the expression, the values it resolved, the
 * threshold it was compared against, and the reference it cites — then the
 * work it produced, and the button to raise more.
 */
function Explanation({ id }: { id: string }) {
  const x = useAlertExplanation(id);
  if (x.isPending) {
    return (
      <Card title="Why did this fire?">
        <Skeleton rows={6} />
      </Card>
    );
  }
  if (x.isError) {
    return (
      <Card title="Why did this fire?">
        <ErrorState error={x.error} retry={() => void x.refetch()} />
      </Card>
    );
  }
  return <ExplanationBody a={x.data} />;
}

function ExplanationBody({ a }: { a: AlertExplanation }) {
  const { principal } = useSession();
  const navigate = useNavigate();
  const [raising, setRaising] = useState(false);
  const canWrite = hasPermission(principal, PERMISSION.workOrdersWrite);
  const orders = useWorkOrders();
  const byNumber = useMemo(() => new Map((orders.data?.items ?? []).map((w) => [w.number, w.id])), [orders.data]);

  const prefill: WorkOrderPrefill = {
    title: a.title,
    kind: a.kind === "predictive" ? "predictive" : "corrective",
    priority: priorityForSeverity(a.severity),
    assetTag: a.assetTag,
    description: [
      a.ruleName ? `Rule: ${a.ruleName} (${a.ruleKey})` : undefined,
      a.subjectLabel ? `Subject: ${a.subjectLabel}` : undefined,
      a.reference ? `Reference: ${a.reference}` : undefined,
    ]
      .filter(Boolean)
      .join("\n"),
    alertId: a.id,
    alertTitle: a.title,
  };

  const thresholds = Object.entries(a.parameters);
  const resolved = Object.entries(a.resolved);

  return (
    <Card
      title="Why did this fire?"
      sub={a.title}
      actions={
        <Button
          variant="primary"
          size="sm"
          disabled={!canWrite}
          onClick={() => setRaising(true)}
          title={
            canWrite
              ? t("New Work Order, pre-filled from this alert")
              : t("Requires {permission}", { permission: PERMISSION.workOrdersWrite })
          }
        >
          {t("+ New Work Order")}
        </Button>
      }
    >
      <div style={{ display: "grid", gap: 14 }} data-testid="explanation">
        <dl className={s.kv}>
          <dt>{t("Rule")}</dt>
          <dd>
            {a.ruleName ?? "—"}
            {a.ruleKey && <span className="muted mono"> ({a.ruleKey})</span>}
          </dd>
          <dt>{t("Class")}</dt>
          <dd>
            {a.ruleClass ?? "—"} · {t(severityLabel(a.severity))} (S{a.severity}) · {t(KIND_WORD[a.kind])}
          </dd>
          <dt>{t("Subject")}</dt>
          <dd>{a.subjectLabel ?? a.assetTag ?? "—"}</dd>
          <dt>{t("Reference")}</dt>
          <dd>{a.reference ?? <span className="muted">{t("none cited")}</span>}</dd>
          <dt>{t("Seen")}</dt>
          <dd>
            {a.occurrences ?? 1}× · {t("first {date}", { date: formatDateTime(a.raisedAt) })}
          </dd>
          {a.kind === "predictive" && (
            <>
              <dt>{t("Expected")}</dt>
              <dd>
                {a.predictedFor ? formatDate(a.predictedFor) : "—"}
                {a.confidence != null && <> · {t("{pct}% confidence", { pct: Math.round(a.confidence * 100) })}</>}
                {a.confirmed === true && <> · {t("confirmed by completed work")}</>}
                {a.confirmed === false && <> · {t("refuted — the work was canceled")}</>}
                {a.confirmed == null && <> · {t("not yet confirmed or refuted")}</>}
              </dd>
            </>
          )}
        </dl>

        {a.verifyRequired && (
          <div className={s.gated}>
            {t(
              "This rule uses a regulatory limit, verified against the airport's certification basis.",
            )}
          </div>
        )}

        <div className={s.section}>
          <div className={s.sectionHead}>{t("Expression")}</div>
          <pre className={s.code}>{a.expression ?? "—"}</pre>
        </div>

        <div className={s.section}>
          <div className={s.sectionHead}>{t("Resolved values")}</div>
          {resolved.length === 0 ? (
            <div className="muted" style={{ fontSize: 13.5 }}>
              {t("The platform recorded no resolved values for this evaluation.")}
            </div>
          ) : (
            <div className={s.resolved}>
              {resolved.map(([k, v]) => {
                const wide = isStructured(v);
                return (
                  <div key={k} className={s.resolvedRow}>
                    <span className={s.resolvedKey}>{k}</span>
                    <span className={`${s.resolvedVal} ${wide ? s.resolvedWide : ""}`}>{fmt(v, wide)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className={s.section}>
          <div className={s.sectionHead}>{t("Thresholds and parameters")}</div>
          {thresholds.length === 0 ? (
            <div className="muted" style={{ fontSize: 13.5 }}>
              {t("None.")}
            </div>
          ) : (
            <div className={s.resolved}>
              {thresholds.map(([k, v]) => (
                <div key={k} className={s.resolvedRow}>
                  <span className={s.resolvedKey}>{k}</span>
                  <span className={`${s.resolvedVal} ${s.threshold}`}>{fmt(v)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={s.section}>
          <div className={s.sectionHead}>{t("Resulting work")}</div>
          {a.resultingActions.length === 0 ? (
            <div className="muted" style={{ fontSize: 13.5 }}>
              {t("No work order has been raised from this alert yet.")}
            </div>
          ) : (
            <div className={s.actions}>
              {a.resultingActions.map((r) => {
                const woId = byNumber.get(r.ref);
                const body = (
                  <>
                    <span className="mono">{r.ref}</span>
                    <span>{r.title}</span>
                    <span className="mono muted">{r.status}</span>
                  </>
                );
                return woId ? (
                  <Link key={r.ref} to={`/work-orders/${woId}`} className={s.actionRow}>
                    {body}
                  </Link>
                ) : (
                  <div key={r.ref} className={s.actionRow}>
                    {body}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {raising && (
        <CreateWorkOrderDialog
          prefill={prefill}
          onClose={() => setRaising(false)}
          onCreated={(wo) => navigate(`/work-orders/${wo.id}`)}
        />
      )}
    </Card>
  );
}

function isStructured(v: unknown): boolean {
  if (Array.isArray(v)) return v.some((x) => typeof x === "object" && x !== null);
  return typeof v === "object" && v !== null;
}

function fmt(v: unknown, wide = false): string {
  if (v == null) return "—";
  if (typeof v === "number")
    return Number.isInteger(v) ? String(v) : v.toFixed(3).replace(/0+$/, "").replace(/\.$/, "");
  if (typeof v === "string") return v;
  if (Array.isArray(v) && v.every((x) => typeof x === "number" || typeof x === "string")) return v.join(", ");
  if (wide && Array.isArray(v)) {
    /* One object per line, "key: value, key: value" — a segment table reads as a table. */
    return v.map((x) => JSON.stringify(x).replace(/[{}"]/g, "").replace(/,/g, ", ").replace(/:/g, ": ")).join("\n");
  }
  return JSON.stringify(v, null, 1).replace(/\n\s*/g, " ");
}
